import { XCircleIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import z from 'zod'
import type { WithOptional } from '../../types.ts'
import { useExternalClassName } from '../../utils/hooks/use-external-class-name.ts'
import * as styles from './input-container.css.ts'

export const ExplicitLabelConditionSchema = z.enum(['required', 'optional'])
export type ExplicitLabelCondition = z.infer<typeof ExplicitLabelConditionSchema>

export const getInputDescriptionId = (id: string) => `${id}-description`

export type InputContainerProps = React.PropsWithChildren<
  WithOptional<InputLabelProps, 'label'> & {
    /** Helper text shown below the input */
    helperText?: string
    externalClassName?: string
    /** Error text shown below the input (replaces helperText when set) */
    errorText?: string
    /** Whether the input is in an error state */
    isError?: boolean
  }
>

export function InputContainer({
  children,
  label,
  id,
  isRequired,
  explicitLabelCondition,
  helperText,
  externalClassName,
  errorText,
  isError,
}: InputContainerProps) {
  const hasError = isError || !!errorText
  const className = useExternalClassName({ external: externalClassName, local: styles.inputWrapper })
  const descriptionId = id ? getInputDescriptionId(id) : undefined

  return (
    <div className={className}>
      {label && (
        <InputLabel
          id={id}
          label={label}
          isRequired={isRequired}
          explicitLabelCondition={explicitLabelCondition}
          isError={hasError}
        />
      )}
      {children}
      {errorText ? (
        <span id={descriptionId} className={styles.errorText}>
          <XCircleIcon size={14} weight="light" />
          {errorText}
        </span>
      ) : (
        helperText && (
          <span id={descriptionId} className={styles.helperText}>
            {helperText}
          </span>
        )
      )}
    </div>
  )
}

type InputLabelProps = {
  /** Label text shown above the input */
  label: string
  /** HTML id attribute. Required for accessibility when using a label */
  id?: string
  /** Whether the input is required */
  isRequired?: boolean
  /**
   * Which state gets an explicit marker next to the label: 'required' shows an
   * asterisk (*) on required inputs, 'optional' shows "(Optional)" on optional inputs
   */
  explicitLabelCondition?: ExplicitLabelCondition
  /** Whether the label should be styled as error */
  isError?: boolean
}

const InputLabel = ({
  id,
  label,
  isRequired = false,
  explicitLabelCondition = 'required',
  isError,
}: InputLabelProps) => {
  const hasRequiredMarker = isRequired && explicitLabelCondition === 'required'
  const hasOptionalMarker = !isRequired && explicitLabelCondition === 'optional'

  return (
    <div className={styles.labelContainer}>
      <label htmlFor={id} className={classNames(styles.inputLabel, { [styles.errorLabel]: isError })}>
        {label}
        {hasRequiredMarker && ' *'}
      </label>
      {hasOptionalMarker && (
        <span className={classNames(styles.optionalText, { [styles.errorLabel]: isError })}>(Optional)</span>
      )}
    </div>
  )
}
